<template>
	<div class="onboarding-dashboard">
		<!-- Page header -->
		<div class="od-header">
			<div class="od-header-left">
				<h1 class="od-title">Dashboard</h1>
				<p class="od-subtitle">Onboarding overview</p>
			</div>
			<div class="od-header-right">
				<NewOnboardingButton @created="getOnboardings" />
			</div>
		</div>

		<!-- Stat cards -->
		<div class="od-stats-grid">
			<div class="od-stat-card od-stat-card--total">
				<div class="od-stat-top">
					<div class="od-stat-chip od-stat-chip--total">
						<font-awesome-icon :icon="['fas', 'list']" />
					</div>
					<div class="od-stat-label">Total</div>
				</div>
				<div class="od-stat-value od-stat-value--total">
					<span v-if="!loading">{{ stats.total }}</span>
					<font-awesome-icon v-else :icon="['fas', 'spinner']" class="fa-spin od-stat-spin" />
				</div>
				<div class="od-stat-note">All onboardings</div>
			</div>

			<div class="od-stat-card od-stat-card--month">
				<div class="od-stat-top">
					<div class="od-stat-chip od-stat-chip--month">
						<font-awesome-icon :icon="['fas', 'calendar-day']" />
					</div>
					<div class="od-stat-label">This Month</div>
				</div>
				<div class="od-stat-value od-stat-value--month">
					<span v-if="!loading">{{ stats.thisMonth }}</span>
					<font-awesome-icon v-else :icon="['fas', 'spinner']" class="fa-spin od-stat-spin" />
				</div>
				<div class="od-stat-note">Created this month</div>
			</div>

			<div class="od-stat-card od-stat-card--types">
				<div class="od-stat-top">
					<div class="od-stat-chip od-stat-chip--types">
						<font-awesome-icon :icon="['fas', 'layer-group']" />
					</div>
					<div class="od-stat-label">Types</div>
				</div>
				<div class="od-stat-value od-stat-value--types">
					<span v-if="!loadingTypes">{{ onboardingTypes.length }}</span>
					<font-awesome-icon v-else :icon="['fas', 'spinner']" class="fa-spin od-stat-spin" />
				</div>
				<div class="od-stat-note">Onboarding types</div>
			</div>
		</div>

		<!-- Widgets -->
		<div class="od-widgets-grid">
			<!-- Latest onboardings -->
			<div class="od-panel">
				<div class="od-panel-header">
					<div class="od-section-title">Latest Onboardings</div>
					<router-link class="od-flat-btn" to="/onboarding/list">
						<font-awesome-icon :icon="['fas', 'list']" />All Onboardings
					</router-link>
				</div>
				<div v-if="loading" class="od-empty"><font-awesome-icon :icon="['fas', 'spinner']" class="fa-spin" /></div>
				<div v-else-if="latestOnboardings.length === 0" class="od-empty">No onboardings found.</div>
				<div v-else class="od-list">
					<router-link v-for="onboarding in latestOnboardings" :key="onboarding.id" class="od-item" :to="'/onboarding/progress/' + onboarding.id">
						<div class="od-item-main">
							<div class="od-item-name">{{ onboarding.name }} <span v-if="onboarding.test" class="badge od-test-badge">TEST</span></div>
							<div class="od-item-sub" v-if="onboarding.type">{{ onboarding.type.name }}</div>
						</div>
						<div class="od-item-right">
							<span class="od-item-date">{{ formatDate(onboarding.created_at) }}</span>
						</div>
					</router-link>
				</div>
			</div>

			<!-- Monthly trend -->
			<div class="od-panel">
				<div class="od-section-title">Monthly Trend <span class="od-section-hint">created, last 12 months</span></div>
				<div v-if="loading" class="od-empty"><font-awesome-icon :icon="['fas', 'spinner']" class="fa-spin" /></div>
				<StatsLineChart v-else :data="monthlyTrendChartData" :options="monthlyTrendChartOptions" :height="160" />
			</div>

			<!-- By type -->
			<div class="od-panel">
				<div class="od-section-title">By Type</div>
				<div v-if="loading" class="od-empty od-empty--sm"><font-awesome-icon :icon="['fas', 'spinner']" class="fa-spin" /></div>
				<div v-else-if="byType.length === 0" class="od-empty od-empty--sm">No onboardings found.</div>
				<div v-else class="od-typebar-list od-typebar-list--compact">
					<div v-for="typeRow in byType" :key="'bt-' + typeRow.name" class="od-typebar">
						<div class="od-typebar-top">
							<span class="od-typebar-name">{{ typeRow.name }}</span>
							<span class="od-typebar-count">{{ typeRow.count }}</span>
						</div>
						<div class="od-track">
							<div class="od-fill" :style="{ width: typeRow.width + '%', background: typeRow.color }"></div>
						</div>
					</div>
				</div>
			</div>

			<!-- Completion progress -->
			<div class="od-panel">
				<div class="od-section-title">Completion Progress <span class="od-section-hint">latest onboardings</span></div>
				<div v-if="loadingCompletion" class="od-empty od-empty--sm"><font-awesome-icon :icon="['fas', 'spinner']" class="fa-spin" /></div>
				<div v-else-if="completionStats.length === 0" class="od-empty od-empty--sm">No completion data available.</div>
				<div v-else class="od-typebar-list od-typebar-list--compact">
					<router-link v-for="row in completionStats" :key="'cp-' + row.id" class="od-typebar od-typebar--link" :to="'/onboarding/progress/' + row.id">
						<div class="od-typebar-top">
							<span class="od-typebar-name">{{ row.name }} <span v-if="row.test" class="badge od-test-badge">TEST</span></span>
							<span class="od-typebar-count">{{ row.filled }}/{{ row.total }} · {{ row.percent }}%</span>
						</div>
						<div class="od-track">
							<div class="od-fill" :style="{ width: row.percent + '%', background: completionColor(row.percent) }"></div>
						</div>
					</router-link>
				</div>
			</div>
		</div>

	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import restApi from '../../api'
import NewOnboardingButton from '../../components/onboarding/NewOnboardingButton.vue'
import StatsLineChart from '../../components/charts/StatsLineChart.vue'

const TYPE_COLORS = ['#2b6cff', '#0f766e', '#6d28d9', '#b45309', '#166534', '#c2410c', '#be185d', '#4338ca']

export default {
	name: 'OnboardingHome',
	data() {
		return {
			loading: false,
			loadingTypes: false,
			loadingCompletion: false,
			onboardings: [],
			onboardingTypes: [],
			completionStats: [],
		}
	},
	components: { NewOnboardingButton, StatsLineChart },
	computed: {
		...mapState(['authUser']),
		...mapGetters(['isAuth']),
		stats() {
			const now = new Date()
			const thisMonth = this.onboardings.filter(o => {
				if (!o.created_at) return false
				const d = new Date(o.created_at)
				return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
			}).length
			return {
				total: this.onboardings.length,
				thisMonth,
			}
		},
		latestOnboardings() {
			return this.onboardings.slice(0, 3)
		},
		monthlyTrend() {
			const now = new Date()
			const months = []
			const byKey = {}
			for (let i = 11; i >= 0; i--) {
				const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
				const month = {
					key: d.getFullYear() + '-' + d.getMonth(),
					label: d.toLocaleDateString('en-US', { month: 'short' }),
					year: d.getFullYear(),
					count: 0,
				}
				months.push(month)
				byKey[month.key] = month
			}
			this.onboardings.forEach(o => {
				if (!o.created_at) return
				const d = new Date(o.created_at)
				const key = d.getFullYear() + '-' + d.getMonth()
				if (byKey[key]) byKey[key].count++
			})
			return months
		},
		monthlyTrendChartData() {
			return {
				labels: this.monthlyTrend.map(m => m.label),
				datasets: [
					{
						label: 'Onboardings',
						data: this.monthlyTrend.map(m => m.count),
						borderColor: '#2b6cff',
						backgroundColor: 'rgba(43, 108, 255, 0.08)',
						fill: true,
						tension: 0.35,
						borderWidth: 2,
						pointRadius: 3,
						pointHoverRadius: 5,
						pointBackgroundColor: '#2b6cff',
					},
				],
			}
		},
		monthlyTrendChartOptions() {
			return {
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					legend: { display: false },
				},
				scales: {
					y: {
						beginAtZero: true,
						ticks: { precision: 0, color: '#9ca3af', font: { size: 11 } },
						grid: { color: '#f1f3f5' },
					},
					x: {
						ticks: { color: '#9ca3af', font: { size: 11 } },
						grid: { display: false },
					},
				},
			}
		},
		byType() {
			const counts = {}
			this.onboardings.forEach(o => {
				const name = o.type ? o.type.name : 'No type'
				counts[name] = (counts[name] || 0) + 1
			})
			const rows = Object.keys(counts)
				.map(name => ({ name, count: counts[name] }))
				.sort((a, b) => b.count - a.count)
			const max = Math.max(1, ...rows.map(r => r.count))
			return rows.map((row, index) => ({
				...row,
				width: Math.round((row.count / max) * 100),
				color: TYPE_COLORS[index % TYPE_COLORS.length],
			}))
		},
	},
	created() {
		this.getOnboardings()
		this.getOnboardingTypes()
		this.getCompletionStats()
	},
	methods: {
		getOnboardings() {
			this.loading = true
			restApi
				.get('/onboarding', { params: { all: true } })
				.then(({ data }) => {
					this.onboardings = data
					this.loading = false
				})
				.catch(error => {
					console.log(error)
					this.loading = false
				})
		},
		getOnboardingTypes() {
			this.loadingTypes = true
			restApi
				.get('/onboarding-type')
				.then(({ data }) => {
					this.onboardingTypes = data
					this.loadingTypes = false
				})
				.catch(error => {
					console.log(error)
					this.loadingTypes = false
				})
		},
		getCompletionStats() {
			this.loadingCompletion = true
			restApi
				.get('/onboarding/completion-stats', { params: { limit: 6 } })
				.then(({ data }) => {
					this.completionStats = data
					this.loadingCompletion = false
				})
				.catch(error => {
					console.log(error)
					this.loadingCompletion = false
				})
		},
		formatDate(dateStr) {
			if (!dateStr) return ''
			const date = new Date(dateStr)
			if (Number.isNaN(date.getTime())) return ''
			return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
		},
		completionColor(percent) {
			if (percent >= 67) return '#1ec872'
			if (percent >= 34) return '#f59e0b'
			return '#2b6cff'
		},
	},
}
</script>

<style scoped>
.onboarding-dashboard {
	padding: 12px 0;
}

/* Header */
.od-header {
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	margin-bottom: 28px;
}

.od-title {
	font-size: 24px;
	font-weight: 700;
	color: #111827;
	margin: 0 0 4px 0;
}

.od-subtitle {
	font-size: 14px;
	color: #6b7280;
	margin: 0;
}

/* Stats grid */
.od-stats-grid {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 16px;
	margin-bottom: 24px;
}

@media (max-width: 900px) {
	.od-stats-grid {
		grid-template-columns: 1fr;
	}
}

.od-stat-card {
	background: #fff;
	border-radius: 12px;
	padding: 14px 16px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
	display: flex;
	flex-direction: column;
	gap: 8px;
	align-items: flex-start;
	justify-content: center;
	min-height: 120px;
	border: 1px solid transparent;
	transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.od-stat-card:hover {
	transform: translateY(-2px);
	box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
}

.od-stat-card--total {
	background: linear-gradient(180deg, #f4f9ff 0%, #e9f2fe 100%);
	border-color: #bfdbfe;
}

.od-stat-card--month {
	background: linear-gradient(180deg, #f6fff8 0%, #eefcf3 100%);
	border-color: #bbf7d0;
}

.od-stat-card--types {
	background: linear-gradient(180deg, #faf7ff 0%, #f3ecfe 100%);
	border-color: #ddd6fe;
}

.od-stat-top {
	display: flex;
	align-items: center;
	gap: 8px;
}

.od-stat-chip {
	width: 24px;
	height: 24px;
	border-radius: 7px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 12px;
}

.od-stat-chip--total {
	color: #1e40af;
	background: #dbeafe;
}

.od-stat-chip--month {
	color: #166534;
	background: #dcfce7;
}

.od-stat-chip--types {
	color: #6d28d9;
	background: #ede9fe;
}

.od-stat-label {
	font-size: 12px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #6b7280;
	margin: 0;
}

.od-stat-value {
	font-size: 30px;
	font-weight: 700;
	line-height: 1;
}

.od-stat-value--total {
	color: #1e40af;
}

.od-stat-value--month {
	color: #166534;
}

.od-stat-value--types {
	color: #6d28d9;
}

.od-stat-spin {
	font-size: 20px;
	color: #9ca3af;
}

.od-stat-note {
	font-size: 12px;
	color: #6b7280;
	line-height: 1.35;
}

/* Section title */
.od-section-title {
	font-size: 13px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: #6b7280;
	margin-bottom: 14px;
}

.od-section-hint {
	font-size: 11px;
	font-weight: 500;
	text-transform: none;
	letter-spacing: 0;
	color: #9ca3af;
	margin-left: 6px;
}

/* Widgets grid */
.od-widgets-grid {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 18px;
}

@media (max-width: 1100px) {
	.od-widgets-grid {
		grid-template-columns: 1fr;
	}
}

.od-panel {
	background: #fff;
	border-radius: 12px;
	padding: 18px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
}

/* Panel header — section title + flat action button */
.od-panel-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 14px;
}

.od-panel-header .od-section-title {
	margin-bottom: 0;
}

/* Gray outline Juniper pill (hqa-link design) as a link */
.od-flat-btn {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 7px 12px;
	border: 1px solid #e0e1e1;
	border-radius: 999px;
	background: #ffffff;
	color: #64748b;
	font-size: 12px;
	font-weight: 600;
	cursor: pointer;
	text-decoration: none;
	transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
	white-space: nowrap;
}

.od-flat-btn:hover {
	background: #f8f9fa;
	border-color: #d1d5db;
	color: #475569;
	text-decoration: none;
}

/* Shared list rows */
.od-empty {
	min-height: 120px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #6b7280;
	font-size: 13px;
	text-align: center;
}

.od-list {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.od-item {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 12px;
	padding: 11px 12px;
	border-radius: 10px;
	background: #f9fafb;
	text-decoration: none;
	border: 1px solid #eef2f7;
	transition: border-color 0.15s ease, background 0.15s ease;
}

.od-item:hover {
	background: #f3f7ff;
	border-color: #dbeafe;
}

.od-item-main {
	min-width: 0;
}

.od-item-name {
	font-size: 13px;
	font-weight: 600;
	color: #111827;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.od-item-sub {
	font-size: 11px;
	color: #6b7280;
	margin-top: 2px;
}

.od-item-right {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-shrink: 0;
}

.od-item-date {
	font-size: 12px;
	color: #6b7280;
	white-space: nowrap;
}

.od-test-badge {
	background: #fef3c7;
	color: #b45309;
	font-size: 9px;
	padding: 2px 6px;
	vertical-align: middle;
}

/* Horizontal bars (By Type / Completion) */
.od-typebar-list {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

/* Compact variant — tighter rows, smaller text, thinner tracks */
.od-typebar-list--compact {
	gap: 7px;
}

.od-typebar-list--compact .od-typebar-name {
	font-size: 12px;
}

.od-typebar-list--compact .od-typebar-count {
	font-size: 11px;
}

.od-typebar-list--compact .od-typebar-top {
	margin-bottom: 3px;
}

.od-typebar-list--compact .od-track {
	height: 5px;
}

.od-empty--sm {
	min-height: 60px;
}

.od-typebar--link {
	text-decoration: none;
	display: block;
	border-radius: 8px;
	padding: 4px 6px;
	margin: -4px -6px;
}

.od-typebar--link:hover {
	background: #f3f7ff;
}

.od-typebar-top {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 12px;
	margin-bottom: 5px;
}

.od-typebar-name {
	font-size: 13px;
	font-weight: 600;
	color: #111827;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.od-typebar-count {
	font-size: 12px;
	color: #6b7280;
	white-space: nowrap;
}

.od-track {
	height: 7px;
	border-radius: 5px;
	background: #eef2f7;
	overflow: hidden;
}

.od-fill {
	height: 100%;
	border-radius: 5px;
	transition: width 0.3s ease;
}

</style>
